import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../../api';
import GroupManager from './GroupManager';
import GroupModal from './GroupModal';

export default function GroupManagerScreen({ token, setError }) {
  const { testId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [test, setTest] = useState(location.state?.test || null);
  const [subjects, setSubjects] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupModalMode, setGroupModalMode] = useState('add');
  const [groupModalData, setGroupModalData] = useState(null);

  const fetchSubjects = async () => {
    try {
      const res = await api.get('/admin/subjects', { headers: { Authorization: `Bearer ${token}` } });
      setSubjects(res.data.subjects);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to fetch subjects');
    }
  };

  const fetchGroups = async () => {
    if (!testId) return;
    try {
      const res = await api.get(`/admin/tests/${testId}/subject-groups`, { headers: { Authorization: `Bearer ${token}` } });
      setGroups(res.data.groups);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to fetch subject groups');
    }
  };

  const fetchTest = async () => {
    if (test) return;
    try {
      const res = await api.get('/admin/tests', { headers: { Authorization: `Bearer ${token}` } });
      const found = res.data.tests.find((t) => String(t.id) === String(testId));
      if (found) setTest(found);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to fetch test details');
    }
  };

  useEffect(() => {
    fetchTest();
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (!test) return;
    fetchGroups();
  }, [test]);

  const saveGroup = async () => {
    if (!test) return;
    try {
      const payload = { name: groupModalData.name, subject_ids: groupModalData.subject_ids || [] };
      if (groupModalMode === 'add') {
        await api.post(`/admin/tests/${test.id}/subject-groups`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await api.put(`/admin/subject-groups/${groupModalData.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      setGroupModalOpen(false);
      fetchGroups();
    } catch (e) {
      setError(e.response?.data?.error || '');
    }
  };

  const deleteGroup = async (id) => {
    try {
      await api.delete(`/admin/subject-groups/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchGroups();
    } catch (e) {
      setError(e.response?.data?.error || '');
    }
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-semibold">Manage Subject Groups</h3>
          <p className="text-sm text-gray-600">Test: {test?.title || test?.name || 'N/A'}</p>
        </div>
        <div className="flex gap-2">
          <button className="bg-gray-200 px-3 py-1 rounded" onClick={() => {
            if (location.state?.from === 'exams') {
              navigate('/exams', {
                state: {
                  orgId: location.state.orgId,
                  typeId: location.state.typeId,
                  cycleId: location.state.cycleId,
                  testId: location.state.testId,
                },
              });
            } else {
              navigate(-1);
            }
          }}>
            Back
          </button>
          <button
            className="bg-green-500 text-white px-3 py-1 rounded"
            onClick={() => {
              setGroupModalMode('add');
              setGroupModalData({ name: '', subject_ids: [] });
              setGroupModalOpen(true);
            }}
          >
            + Add Group
          </button>
        </div>
      </div>

      <GroupManager
        show={true}
        selectedTest={test}
        subjectGroups={groups}
        subjects={subjects}
        onAddGroup={() => {
          setGroupModalMode('add');
          setGroupModalData({ name: '', subject_ids: [] });
          setGroupModalOpen(true);
        }}
        onEditGroup={(group) => {
          setGroupModalMode('edit');
          setGroupModalData({ id: group.id, name: group.name, subject_ids: group.subjects?.map((s) => s.id) || [] });
          setGroupModalOpen(true);
        }}
        onDeleteGroup={deleteGroup}
        onGroupClick={(group) => {
          const subjectIds = group.subjects?.map((s) => s.id) || [];
          navigate('/questions', {
            state: {
              groupId: group.id,
              groupName: group.name,
              subjectIds,
              testId,
            },
          });
        }}
      />

      <GroupModal
        open={groupModalOpen}
        mode={groupModalMode}
        data={groupModalData}
        subjects={subjects}
        onClose={() => setGroupModalOpen(false)}
        onChange={setGroupModalData}
        onSave={saveGroup}
      />
    </div>
  );
}
